import * as React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Monitor, Smartphone } from 'lucide-react';
import { useFocusTrap, usePdfExtractAll } from '@/hooks';
import { Icon, Typography, Spinner } from '@/components/common';
import Icons from '@/const/icons';
import { FieldConfig, RequiredField } from '@/interfaces';
import { ClientSignDocumentView } from '@/components/dashboard/cases/modal-review-sign/ClientSignDocumentView';
import { ClientSignMobileView } from '@/components/dashboard/cases/modal-review-sign/ClientSignMobileView';
import { useClientSignSession } from '@/components/dashboard/cases/modal-review-sign/hooks/useClientSignSession';
import Image from 'next/image';

type PreviewMode = 'desktop' | 'mobile';

const PREVIEW_WIDTH: Record<PreviewMode, number> = {
	desktop: 960,
	mobile: 540,
};
const PREVIEW_PADDING = 24;

export interface DesktopMobilePreviewModalHandle {
	show: () => void;
}

interface DesktopMobilePreviewModalProps {
	pdfUrl: string;
	file: File;
	fieldConfigs: Record<string, FieldConfig>;
	documentName?: string;
	onOpenChange?: (isOpen: boolean) => void;
}

export const DesktopMobilePreviewModal = React.forwardRef<
	DesktopMobilePreviewModalHandle,
	DesktopMobilePreviewModalProps
>(function DesktopMobilePreviewModal(
	{
		pdfUrl,
		file,
		fieldConfigs,
		documentName = 'Settlement Agreement',
		onOpenChange,
	},
	ref
) {
	const [isOpen, setIsOpen] = React.useState(false);
	const [mode, setMode] = React.useState<PreviewMode>('desktop');
	const dialogRef = useFocusTrap<HTMLDivElement>(isOpen);

	const hide = React.useCallback(() => setIsOpen(false), []);
	const show = React.useCallback(() => setIsOpen(true), []);

	React.useImperativeHandle(ref, () => ({ show }), [show]);

	React.useEffect(() => {
		onOpenChange?.(isOpen);
	}, [isOpen, onOpenChange]);

	React.useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') hide();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, hide]);

	const requiredFields = React.useMemo<RequiredField[]>(
		() =>
			Object.entries(fieldConfigs).map(([id, config]) => ({
				question_id: id,
				question: config.renamedLabel,
				response: null,
				response_id: null,
				type: config.dataType,
				field_name: config.fieldName,
			})),
		[fieldConfigs]
	);

	const { data: extractData, isLoading: isExtracting } = usePdfExtractAll(
		file,
		isOpen && mode === 'mobile'
	);
	const extracted = extractData?.data;

	const mobileSession = useClientSignSession({
		applicationId: undefined,
		actionRequiredId: undefined,
		extractedFields: extracted?.form?.fields,
		requiredFields,
		onClose: hide,
		preview: true,
	});

	const { setSignatureFieldId, setShowDecline, setShowSubmitAnyway } =
		mobileSession;
	React.useEffect(() => {
		if (isOpen) return;
		setSignatureFieldId(null);
		setShowDecline(false);
		setShowSubmitAnyway(false);
	}, [isOpen, setSignatureFieldId, setShowDecline, setShowSubmitAnyway]);

	const modalWidth = PREVIEW_WIDTH[mode] + PREVIEW_PADDING * 2;
	const urlSlug = documentName.toLowerCase().trim().replace(/\s+/g, '-');

	if (!isOpen || typeof document === 'undefined') return null;

	return createPortal(
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center p-4"
			style={{ background: 'rgba(71, 71, 71, 0.8)' }}
			onClick={hide}
		>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-label="Client Preview"
				className="flex flex-col bg-white overflow-hidden transition-[width] duration-200"
				style={{
					width: modalWidth,
					maxWidth: '95vw',
					height: mode === 'desktop' ? '85vh' : undefined,
					maxHeight: '85vh',
					borderRadius: 24,
					boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.08)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					className="flex items-center justify-between shrink-0"
					style={{
						padding: '20px 32px',
						borderBottom: '1px solid rgba(13, 6, 22, 0.06)',
					}}
				>
					<div className="flex flex-col gap-0.5">
						<Typography
							type="semibold-16"
							className="!text-[16px] !text-[#0D0616]"
						>
							Client Preview
						</Typography>
						<Typography
							type="regular-14"
							className="!text-[14px] !text-[#6E6A73]"
						>
							How recipients will experience this document
						</Typography>
					</div>

					<div className="flex items-center gap-4">
						<div
							className="flex items-center"
							style={{
								background: '#F4F4F4',
								borderRadius: 999,
								padding: 3,
								gap: 2,
							}}
						>
							{(
								[
									{ value: 'desktop', label: 'Desktop', Icon: Monitor },
									{ value: 'mobile', label: 'Mobile', Icon: Smartphone },
								] as {
									value: PreviewMode;
									label: string;
									Icon: typeof Monitor;
								}[]
							).map(({ value, label, Icon }) => (
								<button
									key={value}
									type="button"
									onClick={() => setMode(value)}
									aria-pressed={mode === value}
									className={clsx(
										'flex items-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
										mode === value ? 'bg-white' : 'bg-transparent'
									)}
									style={{
										borderRadius: 999,
										padding: '6px 16px',
										boxShadow:
											mode === value
												? '0px 1px 4px 0px rgba(8, 15, 52, 0.12)'
												: undefined,
									}}
								>
									<Icon
										size={14}
										color={mode === value ? '#8039DF' : '#6E6A73'}
									/>
									<Typography
										type="semibold-12"
										className={clsx(
											'!text-[12px]',
											mode === value ? '!text-[#8039DF]' : '!text-[#6E6A73]'
										)}
									>
										{label}
									</Typography>
								</button>
							))}
						</div>

						<button
							type="button"
							onClick={hide}
							aria-label="Close"
							className="shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
						>
							<Image src={Icons.close} alt="close" width={14} height={14} />
						</button>
					</div>
				</div>

				<div
					className="flex-1 min-h-0 flex justify-center"
					style={{ background: '#F0F0F0', padding: 24 }}
				>
					<div
						className="flex flex-col min-h-0 overflow-hidden"
						style={{
							width: PREVIEW_WIDTH[mode],
							maxWidth: '100%',
							boxShadow: '0px 2px 16px 0px rgba(0, 0, 0, 0.12)',
							borderRadius: 10,
						}}
					>
						{mode === 'desktop' && (
							<div
								className="flex items-center gap-4 shrink-0"
								style={{ background: '#2B2933', padding: '10px 16px' }}
							>
								<div className="flex items-center gap-1.5 shrink-0">
									<span
										className="rounded-full"
										style={{ width: 10, height: 10, background: '#EF4444' }}
									/>
									<span
										className="rounded-full"
										style={{ width: 10, height: 10, background: '#F59E0B' }}
									/>
									<span
										className="rounded-full"
										style={{ width: 10, height: 10, background: '#22C55E' }}
									/>
								</div>
								<div
									className="flex-1 min-w-0 flex items-center justify-center"
									style={{
										background: 'rgba(255, 255, 255, 0.08)',
										borderRadius: 999,
										padding: '4px 12px',
									}}
								>
									<Typography
										type="regular-12"
										className="!text-[12px] !text-[rgba(255,255,255,0.6)] truncate"
									>
										claimmagic.com/sign/{urlSlug}
									</Typography>
								</div>
							</div>
						)}

						<div
							className="flex items-center justify-between shrink-0"
							style={{
								background: 'white',
								padding: '14px 20px',
								borderBottom: '1px solid rgba(13, 6, 22, 0.06)',
							}}
						>
							<div className="flex items-center gap-2.5 min-w-0">
								<div
									className="flex items-center justify-center shrink-0"
									style={{
										width: 28,
										height: 28,
										borderRadius: 8,
										background: '#8039DF',
									}}
								>
									<Image
										src={Icons.documentText}
										alt="document text"
										width={14}
										height={14}
										className="brightness-0 invert"
									/>
								</div>
								<Typography
									type="semibold-14"
									className="!text-[14px] !text-[#0D0616] truncate"
								>
									Sign {documentName}
								</Typography>
							</div>
							<button
								type="button"
								onClick={hide}
								aria-label="Close"
								className="shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
							>
								<Icon src={Icons.close} width={14} height={14} />
							</button>
						</div>

						{mode === 'desktop' ? (
							<div
								className="flex-1 min-h-0 flex flex-col bg-white"
								style={{ padding: '16px 32px 24px' }}
							>
								<ClientSignDocumentView
									preview
									documentUrl={pdfUrl}
									applicationId={undefined}
									actionRequiredId={undefined}
									requiredFields={requiredFields}
									onClose={hide}
								/>
							</div>
						) : (
							<div className="flex-1 min-h-0 flex flex-col bg-white">
								{isExtracting ? (
									<div className="flex flex-1 items-center justify-center py-16">
										<Spinner />
									</div>
								) : (
									<ClientSignMobileView
										documentUrl={pdfUrl}
										documentName={documentName}
										extractedFields={extracted?.form?.fields}
										textPages={extracted?.text?.pages}
										documentImages={extracted?.vectorImages?.images}
										preview
										onClose={hide}
										session={mobileSession}
									/>
								)}
							</div>
						)}
					</div>
				</div>

				<div
					className="flex items-center gap-2 shrink-0"
					style={{
						padding: '12px 32px',
						borderTop: '1px solid rgba(13, 6, 22, 0.06)',
					}}
				>
					<Icon src={Icons.infoCircle} width={16} height={16} />
					<Typography
						type="regular-12"
						className="!text-[12px] !text-[#6E6A73]"
					>
						This is a preview of how the recipient will see and sign this
						document.
					</Typography>
				</div>
			</div>
		</div>,
		document.body
	) as React.ReactElement;
});

export default DesktopMobilePreviewModal;
